/* Editorial AI artwork. Cutout images are loaded once; scroll changes their transforms. */
(function (window, document) {
  "use strict";
  if (window.SiculaBrioche) return;
  const instances = new WeakMap();

  function clamp(value) { return Math.min(1, Math.max(0, value)); }
  function assetUrl(file, root) {
    if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|\/)/i.test(file)) return file;
    return (root ? root.replace(/\/?$/, "/") : "") + file;
  }
  function prepare(manifest, profile, element) {
    if (!manifest || !Array.isArray(manifest.actorOrder) || !manifest.actorOrder.length ||
        !Array.isArray(manifest.frames) || manifest.frames.length < 2) {
      throw new Error("A motion manifest with actorOrder and at least two frames is required.");
    }
    const order = manifest.actorOrder.slice();
    if (new Set(order).size !== order.length) throw new Error("Each actor ID must be unique.");
    const original = manifest.profiles && manifest.profiles[profile];
    const assets = original && {
      base: element.getAttribute("data-" + profile + "-backdrop") || original.base,
      poster: element.getAttribute("data-" + profile + "-poster") || original.poster,
      actors: Object.assign({}, original.actors)
    };
    order.forEach(function (id) {
      const supplied = element.getAttribute("data-" + profile + "-actor-" + id);
      if (supplied && assets) assets.actors[id] = supplied;
    });
    if (!assets || !assets.base || !assets.poster || !assets.actors ||
        order.some(function (id) { return !assets.actors[id]; })) {
      throw new Error("The selected asset profile is incomplete.");
    }
    const clips = {};
    order.forEach(function (id) {
      const polygon = manifest.actorClipPolygons && manifest.actorClipPolygons[id];
      if (!polygon) return;
      if (!Array.isArray(polygon) || polygon.length < 3 || polygon.some(function (point) {
        return !Array.isArray(point) || point.length !== 2 ||
          point.some(function (value) { return !Number.isFinite(value); });
      })) throw new Error("Actor clip polygons must contain at least three normalized points.");
      clips[id] = "polygon(" + polygon.map(function (point) {
        return point[0] * 100 + "% " + point[1] * 100 + "%";
      }).join(", ") + ")";
    });
    const fractionActors = new Set();
    const frames = manifest.frames.map(function (frame) {
      const layers = {};
      (frame.layers || []).forEach(function (layer) {
        if (!order.includes(layer.id)) throw new Error("Unknown actor in the motion manifest.");
        if (layers[layer.id]) throw new Error("An actor occurs more than once in a frame.");
        const rect = layer.rect;
        if (!Array.isArray(rect) || rect.length !== 4 ||
            rect.some(function (v) { return !Number.isFinite(v); }) || rect[2] < 0 || rect[3] < 0) {
          throw new Error("Actor rectangles must contain four finite normalized values.");
        }
        const rotation = layer.rotation === undefined ? 0 : layer.rotation;
        const opacity = layer.opacity === undefined ? 1 : layer.opacity;
        if (!Number.isFinite(rotation) || !Number.isFinite(opacity)) {
          throw new Error("Actor rotation and opacity must be finite numbers.");
        }
        const clipFraction = layer.clipFraction === undefined ? 1 : layer.clipFraction;
        if (!Number.isFinite(clipFraction)) throw new Error("Actor clip fractions must be finite numbers.");
        if (layer.clipFraction !== undefined) fractionActors.add(layer.id);
        layers[layer.id] = {
          rect: rect.slice(), rotation: rotation, opacity: clamp(opacity),
          clipFraction: clamp(clipFraction)
        };
      });
      return layers;
    });
    return { order: order, assets: assets, frames: frames, clips: clips, fractionActors: fractionActors };
  }

  function mount(element, options) {
    if (!element) throw new Error("An animation container is required.");
    if (instances.has(element)) return instances.get(element);
    options = options || {};
    // A resize does not download a second set of artwork.
    const mobile = window.matchMedia("(max-width: 767px)").matches;
    const profile = mobile ? "mobile" : "desktop";
    const resolution = mobile ? 512 : 768;
    const prepared = prepare(options.manifest || window.SiculaBriocheManifest, profile, element);
    const root = options[profile + "BasePath"] ||
      element.getAttribute("data-" + profile + "-base-path") || "./";
    const reduced = options.respectReducedMotion !== false &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const created = [];
    const images = [];
    const actors = {};
    let progress = 0;
    let started = false;
    let ready = false;
    let stopped = false;
    let destroyed = false;
    let observer = null;
    let queue = [];
    let active = 0;
    let loaded = 0;
    let groupCount = 0;

    function image(className) {
      const node = document.createElement("img");
      node.className = className;
      node.alt = "";
      node.decoding = "async";
      node.draggable = false;
      node.hidden = true;
      node.setAttribute("aria-hidden", "true");
      element.appendChild(node);
      created.push(node);
      images.push(node);
      return node;
    }
    element.classList.add("sicula-brioche");
    const base = image("sicula-brioche__base");
    base.width = resolution;
    base.height = resolution;
    prepared.order.forEach(function (id, index) {
      const actor = image("sicula-brioche__actor");
      actor.setAttribute("data-sicula-actor", id);
      actor.style.zIndex = String(index + 1);
      if (prepared.clips[id]) {
        actor.style.clipPath = prepared.clips[id];
        actor.style.webkitClipPath = prepared.clips[id];
      }
      actors[id] = actor;
    });
    const poster = image("sicula-brioche__poster");
    poster.width = resolution;
    poster.height = resolution;
    poster.style.zIndex = String(prepared.order.length + 1);

    function release(node) {
      node.onload = null;
      node.onerror = null;
      node.removeAttribute("src");
    }
    function showPoster() {
      if (destroyed) return;
      stopped = true;
      queue = [];
      base.hidden = true;
      prepared.order.forEach(function (id) { actors[id].hidden = true; });
      if (!poster.getAttribute("src")) poster.src = assetUrl(prepared.assets.poster, root);
      poster.hidden = false;
    }
    function render() {
      if (!ready || stopped || destroyed) return;
      const position = progress * (prepared.frames.length - 1);
      const lower = Math.floor(position);
      const upper = Math.min(prepared.frames.length - 1, lower + 1);
      const mix = position - lower;
      const first = prepared.frames[lower];
      const second = prepared.frames[upper];
      prepared.order.forEach(function (id) {
        const a = first[id];
        const b = second[id];
        const actor = actors[id];
        if (!a && !b) { actor.hidden = true; return; }
        const source = a || b;
        const target = b || a;
        const rect = source.rect.map(function (v, index) {
          return v + (target.rect[index] - v) * mix;
        });
        const fromOpacity = a ? a.opacity : 0;
        const toOpacity = b ? b.opacity : 0;
        const opacity = fromOpacity + (toOpacity - fromOpacity) * mix;
        actor.style.left = rect[0] * 100 + "%";
        actor.style.top = rect[1] * 100 + "%";
        actor.style.width = rect[2] * 100 + "%";
        actor.style.height = rect[3] * 100 + "%";
        actor.style.transform = "rotate(" + (source.rotation +
          (target.rotation - source.rotation) * mix) + "deg)";
        actor.style.opacity = String(opacity);
        if (prepared.fractionActors.has(id)) {
          const fraction = source.clipFraction + (target.clipFraction - source.clipFraction) * mix;
          const clip = "inset(0 " + ((1 - fraction) * 100) + "% 0 0)";
          actor.style.clipPath = clip;
          actor.style.webkitClipPath = clip;
        }
        actor.hidden = opacity <= 0;
      });
    }
    function pump() {
      while (!destroyed && !stopped && active < 2 && queue.length) {
        const job = queue.shift();
        active += 1;
        const source = job.nodes[0];
        source.onload = async function () {
          try {
            if (source.decode) await source.decode();
            if (destroyed || stopped) return;
            // The front rim reuses the base bitmap after it is loaded. It has no
            // independent network job and receives the identical cached URL.
            await Promise.all(job.nodes.slice(1).map(async function (node) {
              node.src = job.url;
              if (node.decode) await node.decode();
            }));
            if (destroyed || stopped) return;
            active -= 1;
            loaded += 1;
            source.onload = null;
            source.onerror = null;
            if (loaded === groupCount) {
              ready = true;
              base.hidden = false;
              render();
            }
            pump();
          } catch (_) { showPoster(); }
        };
        source.onerror = showPoster;
        source.src = job.url;
      }
    }
    function start() {
      if (started || destroyed) return;
      started = true;
      if (observer) observer.disconnect();
      if (reduced) { showPoster(); return; }
      const groups = new Map();
      function enqueue(node, file) {
        const url = assetUrl(file, root);
        if (!groups.has(url)) groups.set(url, { url: url, nodes: [] });
        groups.get(url).nodes.push(node);
      }
      enqueue(base, prepared.assets.base);
      prepared.order.forEach(function (id) {
        enqueue(actors[id], prepared.assets.actors[id]);
      });
      queue = Array.from(groups.values());
      groupCount = queue.length;
      pump();
    }
    const api = {
      setProgress: function (value) {
        if (destroyed || stopped) return;
        const number = Number(value);
        if (!Number.isFinite(number)) return;
        progress = clamp(number);
        render();
      },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        if (observer) observer.disconnect();
        queue = [];
        images.forEach(release);
        created.forEach(function (node) { node.remove(); });
        instances.delete(element);
      }
    };
    instances.set(element, api);
    // No artwork is requested while the section is far outside the viewport.
    if (window.IntersectionObserver) {
      observer = new window.IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) start();
      }, { rootMargin: "250px 0px", threshold: 0 });
      observer.observe(element);
    } else start();
    return api;
  }
  window.SiculaBrioche = { mount: mount };

  function init() {
    document.querySelectorAll("[data-sicula-brioche]").forEach(function (element) {
      try { mount(element); }
      catch (error) { window.console.warn("Sicula Briosce animation:", error.message); }
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})(window, document);
